import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@cr/shared';
import { api, getRefreshToken, setTokens } from '@/lib/api';
import { currentLocale, setLocale } from '@/lib/i18n';
import { useSession } from '@/lib/session';
import Icon, { type IconName } from '@/components/icon';
import { Badge, Button, Card, EmptyState, Skeleton } from '@/components/ui';

interface Profile {
  id: string;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  phoneConfirmed: boolean;
  myidVerified: boolean;
}

interface Address {
  id: string;
  label: string | null;
  addressLine: string;
  isDefault: boolean;
}

const LOCALE_NAMES: Record<Locale, string> = { ru: 'Русский', uz: 'O‘zbekcha', en: 'English' };

export default function ProfilePage() {
  const { t } = useTranslation();
  const { authorized } = useSession();
  const queryClient = useQueryClient();

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await api.get<Profile>('/auth/me')).data,
    enabled: authorized,
  });

  const addresses = useQuery({
    queryKey: ['addresses'],
    queryFn: async () => (await api.get<Address[]>('/addresses')).data,
    enabled: authorized,
  });

  const removeAddress = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/addresses/${id}`);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['addresses'] }),
  });

  /** Выход разлогинивает и на сервере: refresh-токен должен перестать работать. */
  const logout = useMutation({
    mutationFn: async () => {
      const refreshToken = getRefreshToken();
      if (refreshToken) await api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    },
    onSuccess: () => {
      setTokens(null, null);
      queryClient.clear();
      window.location.reload();
    },
  });

  if (!authorized) {
    return (
      <div className="px-4 pb-8 pt-1 sm:px-6">
        <h1 className="mb-4 font-display text-[26px] font-semibold">{t('profile.title')}</h1>
        <EmptyState title={t('common.outsideTelegram')} />
      </div>
    );
  }

  const user = profile.data;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  return (
    <div className="space-y-4 px-4 pb-8 pt-1 sm:px-6">
      <h1 className="font-display text-[26px] font-semibold">{t('profile.title')}</h1>

      {profile.isLoading ? (
        <Skeleton className="h-24 rounded-card" />
      ) : (
        <Card className="flex items-center gap-3">
          <span className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-full bg-cream">
            {user?.photoUrl ? (
              <img src={user.photoUrl} alt="" className="size-full object-cover" />
            ) : (
              <Icon name="user" size={26} className="text-bordo" />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-lg">{fullName || t('profile.guest')}</p>
            <p className="truncate text-sm text-muted">
              {user?.phone ?? (user?.username ? `@${user.username}` : t('profile.noPhone'))}
            </p>
          </div>

          {user?.myidVerified && <Badge tone="green">{t('profile.verified')}</Badge>}
        </Card>
      )}

      <section>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="font-display text-lg font-semibold">{t('profile.addresses')}</h2>
        </div>

        {addresses.isLoading ? (
          <Skeleton className="h-16 rounded-card" />
        ) : (addresses.data ?? []).length === 0 ? (
          <Card>
            <p className="text-sm text-muted">{t('profile.noAddresses')}</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {addresses.data!.map((address) => (
              <Card key={address.id} className="flex items-center gap-3">
                <Icon name="pin" size={20} className="shrink-0 text-bordo" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px]">{address.addressLine}</p>
                  {address.label && <p className="truncate text-xs text-muted">{address.label}</p>}
                </div>
                {address.isDefault && <Badge tone="gold">{t('profile.defaultAddress')}</Badge>}
                <button
                  type="button"
                  onClick={() => removeAddress.mutate(address.id)}
                  disabled={removeAddress.isPending}
                  aria-label={t('cart.remove')}
                  className="shrink-0 text-muted active:text-error disabled:opacity-40"
                >
                  <Icon name="trash" size={18} />
                </button>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-1">
        <MenuLink to="/favorites" icon="heart" label={t('nav.favorites')} />
        <MenuLink to="/orders" icon="orders" label={t('orders.title')} />
        <MenuLink to="/offer" icon="info" label={t('checkout.offerRead')} />
      </section>

      <NotificationSettings />

      <section>
        <h2 className="mb-2 font-display text-lg font-semibold">{t('profile.language')}</h2>
        <div className="flex gap-2">
          {LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              onClick={() => setLocale(locale)}
              className={`flex-1 rounded-xl py-2.5 text-sm font-medium transition active:scale-95 ${
                currentLocale() === locale
                  ? 'bg-bordo text-white'
                  : 'bg-surface text-ink shadow-soft'
              }`}
            >
              {LOCALE_NAMES[locale]}
            </button>
          ))}
        </div>
      </section>

      <Button
        variant="secondary"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="w-full"
      >
        <Icon name="logout" size={18} />
        {t('profile.logout')}
      </Button>
    </div>
  );
}

function MenuLink({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl bg-surface px-4 py-3.5 shadow-soft active:scale-[0.99]"
    >
      <Icon name={icon} size={20} className="shrink-0 text-bordo" />
      <span className="flex-1 text-[15px]">{label}</span>
      <Icon name="chevron" size={18} className="shrink-0 text-muted" />
    </Link>
  );
}

/** События, от которых можно отписаться. Критичные сюда не попадают: сервер их всё равно не отключит. */
const OPTIONAL_EVENTS = [
  'RENTAL_STARTED',
  'RETURN_REMINDER',
  'ITEM_RETURNED',
  'INSPECTION_STARTED',
  'ORDER_COMPLETED',
  'DEPOSIT_REFUNDED',
] as const;

function NotificationSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const prefs = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: async () => (await api.get<{ disabled: string[] }>('/notifications/preferences')).data,
  });

  const toggle = useMutation({
    mutationFn: async (input: { event: string; enabled: boolean }) =>
      api.post('/notifications/preferences', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-prefs'] }),
  });

  const disabled = new Set(prefs.data?.disabled ?? []);

  return (
    <section>
      <h2 className="mb-2 font-display text-lg font-semibold">{t('profile.notifications')}</h2>
      <p className="mb-2 text-xs text-muted">{t('profile.notificationsHint')}</p>

      <div className="space-y-1">
        {OPTIONAL_EVENTS.map((event) => {
          const enabled = !disabled.has(event);

          return (
            <button
              key={event}
              type="button"
              disabled={toggle.isPending}
              onClick={() => toggle.mutate({ event, enabled: !enabled })}
              className="flex w-full items-center gap-3 rounded-xl bg-surface px-4 py-3 text-left shadow-soft active:scale-[0.99]"
            >
              <span className="flex-1 text-[15px]">{t(`profile.event.${event}`)}</span>
              <span
                className={`grid h-6 w-10 shrink-0 items-center rounded-pill px-0.5 transition ${
                  enabled ? 'bg-bordo' : 'bg-line'
                }`}
              >
                <span
                  className={`size-5 rounded-full bg-surface shadow-soft transition ${
                    enabled ? 'translate-x-4' : ''
                  }`}
                />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
